// shader-canvas.js
// Add <canvas data-shader="path/to/shader.frag"> and it just works.
//
// Attributes
//   data-shader   URL of the fragment shader
//   data-max-dpr  optional cap on devicePixelRatio (default 2)
//
// State (set by the script, style it with CSS)
//   no data-state       loading
//   data-state="ready"  first real frame drawn
//   data-state="error"  WebGL unavailable, fetch failed, or shader failed to compile/link
//
// Uniforms set automatically (declare whichever you use)
//   uniform float u_time;        // seconds; freezes while offscreen or with prefers-reduced-motion
//   uniform vec2  u_resolution;  // buffer size in pixels
//   uniform vec2  u_mouse;       // pointer in buffer pixels, origin bottom-left, relative to this canvas.
//                                // Tracked on the whole window, so it can be outside 0..u_resolution.
//                                // Starts at the canvas center until the pointer first moves.

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

// Added only when the shader declares no float precision. #line 1 keeps
// compiler error line numbers matching your .frag file.
const FRAG_HEADER = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
#line 1
`;

// One oversized triangle covers the whole viewport.
const TRIANGLE = new Float32Array([-1, -1, 3, -1, -1, 3]);

export class ShaderCanvas {
  static async from(canvas) {
    const url = canvas.dataset.shader;
    const res = await fetch(url);
    if (!res.ok)
      throw new Error(`ShaderCanvas: failed to load ${url} (${res.status})`);
    return new ShaderCanvas(canvas, await res.text());
  }

  constructor(canvas, source) {
    this.canvas = canvas;
    this.maxDpr = parseFloat(canvas.dataset.maxDpr) || 2;
    this.source = /precision\s+\w+\s+float/.test(source)
      ? source
      : FRAG_HEADER + source;

    this.time = 0;
    this.pointer = null; // last pointer position in client px
    this.visible = false;
    this._raf = 0;
    this._startTime = 0;

    // Reduced motion: no loop, u_time stays put. Re-checked live.
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = motion.matches;
    motion.addEventListener('change', (e) => {
      this.reducedMotion = e.matches;
      this._update();
    });

    this._setupGL();

    new ResizeObserver(() => this._resize()).observe(canvas);
    new IntersectionObserver((entries) => {
      this.visible = entries.at(-1).isIntersecting;
      this._update();
    }).observe(canvas);
    this._watchDpr();

    window.addEventListener(
      'pointermove',
      (e) => {
        this.pointer = { x: e.clientX, y: e.clientY };
        if (this.reducedMotion && this.visible) this._draw(); // no loop running, so redraw on input
      },
      { passive: true },
    );

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault(); // tells the browser we want the context back
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    });
    canvas.addEventListener('webglcontextrestored', () => {
      this._setupGL();
      this._resize();
      this._update();
    });

    this._resize(); // first frame
  }

  _fail() {
    this.program = null;
    this.canvas.dataset.state = 'error';
  }

  _setupGL() {
    const gl = (this.gl = this.canvas.getContext('webgl', {
      antialias: false,
      alpha: true,
      premultipliedAlpha: true,
      powerPreference: 'low-power',
    }));
    if (!gl) {
      console.error(
        `ShaderCanvas (${this.canvas.dataset.shader}): WebGL not available`,
      );
      return this._fail();
    }

    let ok = true;
    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        ok = false;
        console.error(
          `ShaderCanvas (${this.canvas.dataset.shader}):\n${gl.getShaderInfoLog(sh)}`,
        );
      }
      return sh;
    };

    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, this.source));
    gl.linkProgram(prog);
    if (!ok || !gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      if (ok)
        console.error(
          `ShaderCanvas (${this.canvas.dataset.shader}) link failed:\n${gl.getProgramInfoLog(prog)}`,
        );
      return this._fail();
    }
    this.program = prog;
    gl.useProgram(prog);
    gl.clearColor(0.0, 0.0, 0.0, 0.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, TRIANGLE, gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    // null if the shader doesn't declare it; setting a null location is a no-op
    this.u = {
      time: gl.getUniformLocation(prog, 'u_time'),
      resolution: gl.getUniformLocation(prog, 'u_resolution'),
      mouse: gl.getUniformLocation(prog, 'u_mouse'),
    };
  }

  // devicePixelRatio changes (e.g. window moved to another monitor) don't resize the
  // element, so watch for it explicitly. The query is re-armed after each change.
  _watchDpr() {
    matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener(
      'change',
      () => {
        this._resize();
        this._watchDpr();
      },
      { once: true },
    );
  }

  _resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, this.maxDpr);
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w; // assigning these clears the buffer...
      this.canvas.height = h;
    }
    if (this.gl && !this.gl.isContextLost()) this.gl.viewport(0, 0, w, h);
    this._draw(); // ...so always redraw right away to avoid a blank frame
  }

  _draw() {
    const gl = this.gl;
    if (!gl || !this.program || gl.isContextLost()) return;

    const { width: w, height: h } = this.canvas;
    let mx = w / 2,
      my = h / 2;
    if (this.pointer) {
      const r = this.canvas.getBoundingClientRect();
      mx = (this.pointer.x - r.left) * (w / (r.width || 1));
      my = (r.bottom - this.pointer.y) * (h / (r.height || 1));
    }

    gl.useProgram(this.program);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.uniform1f(this.u.time, this.time);
    gl.uniform2f(this.u.resolution, w, h);
    gl.uniform2f(this.u.mouse, mx, my);

    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (this.canvas.dataset.state !== 'ready' && this.canvas.clientWidth > 0) {
      this.canvas.dataset.state = 'ready';
    }
  }

  _frame = (now) => {
    this.time = (now - this._startTime) / 1000;
    this._draw();
    this._raf = requestAnimationFrame(this._frame);
  };

  // Reconcile the loop with visibility / reduced motion / context state.
  _update() {
    const run =
      this.visible &&
      !this.reducedMotion &&
      this.program &&
      !this.gl.isContextLost();
    if (run && !this._raf) {
      // Resume from the current u_time so pausing never makes time jump.
      this._startTime = performance.now() - this.time * 1000;
      this._raf = requestAnimationFrame(this._frame);
    } else if (!run && this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
  }
}

export function initShaderCanvases(root = document) {
  return Promise.all(
    [...root.querySelectorAll('canvas[data-shader]')].map((c) =>
      ShaderCanvas.from(c).catch((err) => {
        console.error(err);
        c.dataset.state = 'error';
        return null;
      }),
    ),
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initShaderCanvases());
} else {
  initShaderCanvases();
}
